import React, { useState, useEffect } from "react";
import { getTimeZones } from "@vvo/tzdb";
import * as QRCode from "qrcode";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
} from "@mui/material";
import { config } from "../config";

type OperatingHours = Record<string, { open: string; close: string } | null>;

type AccountUpdateData = {
  address: string;
  phone: string;
  email: string;
  description: string;
  timezone: string;
  operating_hours: OperatingHours;
};

interface AccountProps {
  name: string;
  address: string;
  phone: string;
  email: string;
  operating_hours: OperatingHours | null | undefined;
  description: string;
  timezone: string;
  slug: string;

  logoUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;

  onSave: (updatedData: AccountUpdateData) => void;
}

const daysOfWeek = [
  "Monday", "Tuesday", "Wednesday",
  "Thursday", "Friday", "Saturday", "Sunday",
];

// ⭐ Normalize ANY shape returned by backend
const normalizeOperatingHours = (
  hours: OperatingHours | null | undefined
) => {
  const result: OperatingHours = {};

  daysOfWeek.forEach((day) => {
    const row = hours?.[day];

    if (row && typeof row.open === "string" && typeof row.close === "string") {
      result[day] = { open: row.open, close: row.close };
    } else {
      result[day] = null; // Closed by default
    }
  });

  return result;
};

const timezoneOptions = getTimeZones().map((tz) => ({
  label: `${tz.name} (UTC${(tz.currentTimeOffsetInMinutes / 60).toFixed(1)})`,
  value: tz.name,
}));

const buildBookingUrl = (slug: string) => `${config.bookingPageBaseUrl}/${slug}`;

const AccountTable: React.FC<AccountProps> = ({
  name,
  address,
  phone,
  email,
  operating_hours,
  description,
  timezone,
  slug,
  logoUrl,
  primaryColor,
  secondaryColor,
  onSave,
}) => {
  // ⭐ Use normalizer so UI always has Monday–Sunday
  const [formData, setFormData] = useState({
    address,
    phone,
    email,
    description,
    timezone,
    operating_hours: normalizeOperatingHours(operating_hours),
  });

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string>("");

useEffect(() => {
  if (!slug) return;

  let cancelled = false;
  const url = buildBookingUrl(slug);

  QRCode.toDataURL(url, { width: 160 })
    .then((dataUrl: string) => {
      if (!cancelled) setQrDataUrl(dataUrl);
    })
    .catch(() => {
      if (!cancelled) setQrDataUrl("");
    });

  return () => {
    cancelled = true;
  };
}, [slug]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleHoursChange = (day: string, field: "open" | "close", value: string) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: {
          ...(prev.operating_hours[day] ?? { open: "08:00", close: "16:00" }),
          [field]: value,
        },
      },
    }));
  };

  const toggleClosed = (day: string) => {
    setFormData((prev) => ({
      ...prev,
      operating_hours: {
        ...prev.operating_hours,
        [day]: prev.operating_hours[day] ? null : { open: "08:00", close: "16:00" },
      },
    }));
  };

  const handleSave = async () => {
    try {
      await onSave({
        ...formData,
        operating_hours: formData.operating_hours,
      });

      setSnackbarOpen(true);
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Failed to save:", error);
    }
  };

  const brandPrimary = primaryColor || "#111827";
  const brandSecondary = secondaryColor || "#f97316";
  const bookingUrl = slug ? buildBookingUrl(slug) : "";
  const widgetCode = slug
    ? `<script
  src="${config.bookingPageBaseUrl}/setin-webchat.js"
  data-slug="${slug}"
  data-label="Chat"
  async
></script>`
    : "";

  return (
    <div style={{ padding: 0 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Account Details</Typography>

      {/* ACCOUNT TABLE */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "hidden", boxShadow: 2 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Business Name</TableCell>
              <TableCell>{name}</TableCell>
            </TableRow>

            {/* BOOKING LINK + QR */}
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", verticalAlign: "top" }}>
                Booking Link
              </TableCell>
              <TableCell>
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 220px" },
                  gap: 2,
                  alignItems: "start",
                }}>
                  {/* LEFT COL */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography sx={{ fontSize: "0.9rem", wordBreak: "break-all" }}>
                      {bookingUrl || `${config.bookingPageBaseUrl}/your-business`}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          navigator.clipboard?.writeText(bookingUrl)
                        }
                        disabled={!bookingUrl}
                      >
                        Copy
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          window.open(
                            bookingUrl,
                            "_blank"
                          )
                        }
                        disabled={!bookingUrl}
                      >
                        Open
                      </Button>
                    </Box>

                    <Paper
                      variant="outlined"
                      sx={{
                        mt: 1,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "#f8fafc",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, mb: 0.5 }}>
                        Website chat widget
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem", color: "#475569", mb: 1.5 }}>
                        Add this code before the closing body tag on your website. If someone else manages your site, send them this snippet.
                      </Typography>

                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1.5,
                          borderRadius: 1.5,
                          backgroundColor: "#0f172a",
                          color: "#e2e8f0",
                          fontSize: "0.78rem",
                          overflowX: "auto",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {widgetCode || "Create your booking link first to generate your widget code."}
                      </Box>

                      <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            navigator.clipboard?.writeText(widgetCode);
                            setEmbedCopied(true);
                          }}
                          disabled={!widgetCode}
                        >
                          Copy widget code
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open(`${bookingUrl}/chat`, "_blank")}
                          disabled={!bookingUrl}
                        >
                          Open full-page chat
                        </Button>
                      </Box>
                    </Paper>
                  </Box>

                  {/* RIGHT COL — QR */}
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      textAlign: "center",
                      borderRadius: 3,
                      border: `2px solid ${brandPrimary}`,
                    }}
                  >
                    {logoUrl && (
                      <img
                        src={logoUrl}
                        alt="Logo"
                        style={{
                          width: 50,
                          height: 50,
                          objectFit: "cover",
                          borderRadius: 12,
                          marginBottom: 8,
                        }}
                      />
                    )}

                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 1, color: brandPrimary }}>
                      Scan to Book
                    </Typography>

                    {qrDataUrl && (
                      <img
                        src={qrDataUrl}
                        alt="QR"
                        style={{
                          width: 140,
                          height: 140,
                          borderRadius: 8,
                          border: `2px solid ${brandSecondary}`,
                          marginBottom: 10,
                        }}
                      />
                    )}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const a = document.createElement("a");
                          a.href = qrDataUrl;
                          a.download = `${slug}_qr.png`;
                          a.click();
                        }}
                      >
                        Download
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const win = window.open("");
                          if (!win) return;
                          win.document.write(`
                            <html>
                              <body style="display:flex;justify-content:center;padding:40px;">
                                <img src="${qrDataUrl}" style="width:280px;height:280px;" />
                              </body>
                            </html>
                          `);
                          win.document.close();
                          win.print();
                        }}
                      >
                        Print
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              </TableCell>
            </TableRow>

            {/* BASIC FIELDS */}
            <TableRow><TableCell sx={{ fontWeight: "bold" }}>Address</TableCell><TableCell>{address}</TableCell></TableRow>
            <TableRow><TableCell sx={{ fontWeight: "bold" }}>Phone</TableCell><TableCell>{phone}</TableCell></TableRow>
            <TableRow><TableCell sx={{ fontWeight: "bold" }}>Email</TableCell><TableCell>{email}</TableCell></TableRow>
            <TableRow><TableCell sx={{ fontWeight: "bold" }}>Description</TableCell><TableCell>{description}</TableCell></TableRow>
            <TableRow><TableCell sx={{ fontWeight: "bold" }}>Timezone</TableCell><TableCell>{timezone}</TableCell></TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* OPERATING HOURS */}
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Operating Hours</Typography>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f4f4f4" }}>
              <TableCell>Day</TableCell>
              <TableCell>Open</TableCell>
              <TableCell>Close</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {daysOfWeek.map((day) => (
              <TableRow key={day}>
                <TableCell>{day}</TableCell>
                <TableCell>{formData.operating_hours?.[day]?.open ?? "Closed"}</TableCell>
                <TableCell>{formData.operating_hours?.[day]?.close ?? "Closed"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Button variant="contained" sx={{ mt: 2 }} onClick={() => setEditDialogOpen(true)}>
        Edit Account
      </Button>

      {/* EDIT DIALOG */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Account</DialogTitle>

        <DialogContent>
          <TextField label="Address" fullWidth margin="dense" name="address" value={formData.address} onChange={handleChange} />
          <TextField label="Phone" fullWidth margin="dense" name="phone" value={formData.phone} onChange={handleChange} />
          <TextField label="Email" fullWidth margin="dense" name="email" value={formData.email} onChange={handleChange} />
          <TextField label="Description" fullWidth margin="dense" multiline name="description" value={formData.description} onChange={handleChange} />

          <FormControl fullWidth margin="dense">
            <InputLabel>Timezone</InputLabel>
            <Select
              name="timezone"
              value={formData.timezone}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, timezone: e.target.value }))
              }
            >
              {timezoneOptions.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* EDIT HOURS */}
          {/* EDIT HOURS */}
          <Typography sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Operating Hours
          </Typography>

          <Box sx={{ maxHeight: 320, overflowY: "auto", pr: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "30%" }}>Day</TableCell>
                  <TableCell sx={{ width: "25%" }}>Open</TableCell>
                  <TableCell sx={{ width: "25%" }}>Close</TableCell>
                  <TableCell sx={{ width: "20%" }}>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {daysOfWeek.map((day) => {
                  const row = formData.operating_hours[day];

                  return (
                    <TableRow key={day}>
                      <TableCell>{day}</TableCell>

                      {/* OPEN TIME */}
                      <TableCell>
                        <TextField
                          type="time"
                          size="small"
                          fullWidth
                          disabled={!row}
                          value={row?.open ?? ""}
                          onChange={(e) =>
                            handleHoursChange(day, "open", e.target.value)
                          }
                        />
                      </TableCell>

                      {/* CLOSE TIME */}
                      <TableCell>
                        <TextField
                          type="time"
                          size="small"
                          fullWidth
                          disabled={!row}
                          value={row?.close ?? ""}
                          onChange={(e) =>
                            handleHoursChange(day, "close", e.target.value)
                          }
                        />
                      </TableCell>

                      {/* TOGGLE BUTTON */}
                      <TableCell>
                        <Button
                          fullWidth
                          size="small"
                          variant={row ? "outlined" : "contained"}
                          color={row ? "warning" : "success"}
                          onClick={() => toggleClosed(day)}
                        >
                          {row ? "Close" : "Open"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity="success">Account updated successfully!</Alert>
      </Snackbar>

      <Snackbar open={embedCopied} autoHideDuration={2500} onClose={() => setEmbedCopied(false)}>
        <Alert severity="success">Widget code copied!</Alert>
      </Snackbar>
    </div>
  );
};

export default AccountTable;
